import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { toast } from 'react-toastify';

const AddAidantDialog = ({ open, onClose, onAidantAdded, apiUrl, token }) => {
  const [creationMode, setCreationMode] = useState('manual');

  // Formulaire unifié (contient les infos de l'aidant et le tableau des patients)
  const [formData, setFormData] = useState({
    nom_aidant: '',
    prenom_aidant: '',
    email_aidant: '',
    telephone_aidant: '',
    ville_aidant: '',
    profession_aidant: '',
    adresse_aidant: '',
    patients: [], 
  });

  // États pour le mode CLIENT
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);

  // États temporaires pour l'affectation d'un patient (commun aux 2 modes)
  const [availablePatients, setAvailablePatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [lienPatient, setLienPatient] = useState('');
  const [estPrincipal, setEstPrincipal] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const resPatients = await axios.get(`${apiUrl}/admin/patients`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const rawPatients = Array.isArray(resPatients.data)
            ? resPatients.data
            : resPatients.data?.data || resPatients.data?.patients || [];
          setAvailablePatients(rawPatients.filter(Boolean));

          setLoadingClients(true);
          const resClients = await axios.get(`${apiUrl}/admin/clients/list/all`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const rawClients = Array.isArray(resClients.data?.result)
            ? resClients.data.result
            : Array.isArray(resClients.data)
            ? resClients.data
            : resClients.data?.data || resClients.data?.clients || [];
          setAvailableClients(rawClients.filter(Boolean));
        } catch (err) {
          console.error('Erreur chargement des données :', err);
          toast.error('Impossible de charger les listes.');
        } finally {
          setLoadingClients(false);
        }
      };
      fetchData();
    }
  }, [open, apiUrl, token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);

    // Utilisation de la conversion souple (==) pour éviter les bugs de types
    const clientObj = availableClients.find((c) => c.id_client == clientId);
    if (clientObj) {
      setFormData((prev) => ({
        ...prev,
        nom_aidant: clientObj.nom_client || '',
        prenom_aidant: clientObj.prenom_client || '',
        email_aidant: clientObj.email_client || '',
        telephone_aidant: clientObj.phone_client || '',
        ville_aidant: clientObj.ville_client || '',
        adresse_aidant: clientObj.addresse_client || '',
        patients: [], // Reset des patients si on change de client
      }));
    }
  };

  const handleAddPatientRelation = () => {
    if (!selectedPatientId) {
      toast.warning('Veuillez sélectionner un patient.');
      return;
    }
    if (formData.patients.some((p) => p.patient_profile_id === Number(selectedPatientId))) {
      toast.warning('Ce patient est déjà rattaché à cet aidant.');
      return;
    }
    setFormData({
      ...formData,
      patients: [
        ...formData.patients,
        {
          patient_profile_id: Number(selectedPatientId),
          lien_patient_aidant: lienPatient || 'Autre',
          est_principal: estPrincipal,
        },
      ],
    });
    setSelectedPatientId('');
    setLienPatient('');
    setEstPrincipal(false);
  };

  const handleRemovePatientRelation = (id) => {
    setFormData({
      ...formData,
      patients: formData.patients.filter((p) => p.patient_profile_id !== id),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (creationMode === 'client') {
        if (!selectedClientId) {
          toast.warning('Veuillez sélectionner un client.');
          setIsSubmitting(false);
          return;
        }

        const endpoint = `${apiUrl}/admin/clients/${selectedClientId}/aidants`;
        await axios.post(endpoint, { patients: formData.patients }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success('Aidant créé depuis le client avec succès !');
      } else {
        const endpoint = `${apiUrl}/admin/aidants`;
        await axios.post(endpoint, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success('Aidant et affectations ajoutés avec succès !');
      }

      onAidantAdded();
      handleClose();
    } catch (err) {
      console.error("Erreur lors de l'ajout :", err);
      toast.error(err.response?.data?.message || "Erreur lors de l'ajout de l'aidant.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCreationMode('manual');
    setSelectedClientId('');
    setFormData({
      nom_aidant: '',
      prenom_aidant: '',
      email_aidant: '',
      telephone_aidant: '',
      ville_aidant: '',
      profession_aidant: '',
      adresse_aidant: '',
      patients: [],
    });
    setSelectedPatientId('');
    setLienPatient('');
    setEstPrincipal(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, color: '#2B3674', fontSize: '1.25rem', pb: 1 }}>
          Ajouter un Aidant
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#E0E5F2', px: 3, py: 3 }}>
          <Stack spacing={2.5}>
            
            {/* SÉLECTEUR DE MODE */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <ToggleButtonGroup
                value={creationMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setCreationMode(newMode);
                    setFormData({
                      nom_aidant: '',
                      prenom_aidant: '',
                      email_aidant: '',
                      telephone_aidant: '',
                      ville_aidant: '',
                      profession_aidant: '',
                      adresse_aidant: '',
                      patients: [],
                    });
                    setSelectedClientId('');
                  }
                }}
                size="small"
              >
                <ToggleButton value="manual" sx={{ px: 3, textTransform: 'none', fontWeight: 600 }}>
                  1. Saisie Manuelle (Indépendant)
                </ToggleButton>
                <ToggleButton value="client" sx={{ px: 3, textTransform: 'none', fontWeight: 600 }}>
                  2. Depuis un Client existant
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* SI MODE CLIENT : Sélection du client source */}
            {creationMode === 'client' && (
              <Box sx={{ p: 2, bgcolor: '#F4F7FE', borderRadius: '12px', border: '1px solid #E0E5F2' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2B3674', mb: 1.5 }}>
                  Choisir le client source
                </Typography>
                <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: '8px' }}>
                  <InputLabel>Sélectionner un Client</InputLabel>
                  <Select
                    value={selectedClientId}
                    label="Sélectionner un Client"
                    onChange={handleClientChange}
                    required
                  >
                    {loadingClients ? (
                      <MenuItem disabled>Chargement des clients...</MenuItem>
                    ) : (
                      availableClients.map((client) => (
                        <MenuItem key={client.id_client} value={client.id_client}>
                          {client.nom_client} {client.prenom_client} ({client.email_client})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* CHAMPS DE L'AIDANT */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField 
                label="Nom" 
                name="nom_aidant" 
                value={formData.nom_aidant} 
                onChange={handleChange} 
                required 
                fullWidth 
                size="small" 
                InputProps={{ readOnly: creationMode === 'client' }}
              />
              <TextField 
                label="Prénom" 
                name="prenom_aidant" 
                value={formData.prenom_aidant} 
                onChange={handleChange} 
                required 
                fullWidth 
                size="small" 
                InputProps={{ readOnly: creationMode === 'client' }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField 
                label="Email" 
                name="email_aidant" 
                type="email" 
                value={formData.email_aidant} 
                onChange={handleChange} 
                required 
                fullWidth 
                size="small" 
                InputProps={{ readOnly: creationMode === 'client' }}
              />
              <TextField 
                label="Téléphone" 
                name="telephone_aidant" 
                value={formData.telephone_aidant} 
                onChange={handleChange} 
                fullWidth 
                size="small" 
                InputProps={{ readOnly: creationMode === 'client' }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField 
                label="Ville" 
                name="ville_aidant" 
                value={formData.ville_aidant} 
                onChange={handleChange} 
                fullWidth 
                size="small" 
                InputProps={{ readOnly: creationMode === 'client' }}
              />
              <TextField 
                label="Profession" 
                name="profession_aidant" 
                value={formData.profession_aidant} 
                onChange={handleChange} 
                fullWidth 
                size="small" 
              />
            </Stack>

            <TextField 
              label="Adresse" 
              name="adresse_aidant" 
              value={formData.adresse_aidant} 
              onChange={handleChange} 
              fullWidth 
              size="small" 
              InputProps={{ readOnly: creationMode === 'client' }}
            />

            <Divider sx={{ my: 1 }} />

            {/* SECTION AFFECTATION DE PATIENTS */}
            {(creationMode === 'manual' || (creationMode === 'client' && selectedClientId)) && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B3674', mb: 1.5 }}>
                  Affecter un ou plusieurs patients (Optionnel)
                </Typography>

                <Stack spacing={2} sx={{ p: 2, bgcolor: '#F4F7FE', borderRadius: '12px', border: '1px solid #E0E5F2' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: '8px' }}>
                      <InputLabel>Sélectionner un patient</InputLabel>
                      <Select
                        value={selectedPatientId}
                        label="Sélectionner un patient"
                        onChange={(e) => setSelectedPatientId(e.target.value)}
                      >
                        {availablePatients.length === 0 ? (
                          <MenuItem disabled>Aucun patient disponible</MenuItem>
                        ) : (
                          availablePatients.map((patient) => (
                            <MenuItem key={patient.id_patient_profile} value={patient.id_patient_profile}>
                              {patient.nom_patient} {patient.prenom_patient}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Lien (ex: Frère, Père...)"
                      size="small"
                      value={lienPatient}
                      onChange={(e) => setLienPatient(e.target.value)}
                      fullWidth
                      sx={{ bgcolor: '#fff', borderRadius: '8px' }}
                    />
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={estPrincipal}
                          onChange={(e) => setEstPrincipal(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#2B3674' }}>Patient Principal</Typography>}
                    />

                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddPatientRelation}
                      sx={{
                        backgroundColor: '#4318FF',
                        fontWeight: 700,
                        borderRadius: '8px',
                        textTransform: 'none',
                        px: 3,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#3311CC', boxShadow: 'none' },
                      }}
                    >
                      Associer
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* LISTE DES PATIENTS RATTACHÉS */}
            {formData.patients.length > 0 && (
              <Box sx={{ p: 2, bgcolor: '#EEF2F6', borderRadius: '12px' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#A3AED0', textTransform: 'uppercase' }}>
                  Patients rattachés ({formData.patients.length}) :
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {formData.patients.map((item) => {
                    const patObj = availablePatients.find(
                      (p) => p.id_patient_profile == item.patient_profile_id
                    );
                    return (
                      <Stack
                        key={item.patient_profile_id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ bgcolor: '#fff', p: 1.5, borderRadius: '8px', border: '1px solid #E0E5F2' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2B3674' }}>
                          {patObj ? `${patObj.nom_patient} ${patObj.prenom_patient}` : `Patient ID: ${item.patient_profile_id}`}
                          <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', ml: 1 }}>
                            ({item.lien_patient_aidant}{item.est_principal ? ' - Principal' : ''})
                          </Typography>
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemovePatientRelation(item.patient_profile_id)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Retirer
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}

          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3 }}>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={isSubmitting}
            sx={{ fontWeight: 700, textTransform: 'none', color: '#A3AED0' }}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#4318FF',
              fontWeight: 700,
              borderRadius: '10px',
              textTransform: 'none',
              px: 3.5,
              py: 1,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#3311CC', boxShadow: 'none' },
            }}
          >
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddAidantDialog;